import { useCallback, useEffect, useRef, useState } from "react";
import { dashboardApi, apiErrorMessage } from "@/lib/api";

// ─── Shapes returned by GET /api/dashboard (DashboardController::index) ─────────

export interface DashboardEvent {
  id: number;
  name: string;
  event_type: string | null;
  status: string;
  start_date: string | null; // YYYY-MM-DD
  end_date: string | null; // YYYY-MM-DD (same as start for one-day events)
  guest_count: number;
  client: { id: number; name: string } | null;
  venue: { id: number; name: string } | null;
}

export interface DashboardTask {
  id: number;
  title: string;
  status: string;
  priority: string | null;
  due_date: string | null;
  overdue: boolean;
  assigned_to: string | null;
  event: { id: number; name: string } | null;
}

export interface DashboardQuotation {
  id: number;
  quotation_number: string | null;
  name: string | null;
  client_name: string | null;
  event_date: string | null;
  total_amount: number;
  status: string;
  created_at: string | null;
}

export interface DashboardMonth {
  key: string; // YYYY-MM
  label: string; // "Sep"
  revenue: number;
  profit: number;
  events: number;
  received: number;
  spent: number;
}

/** Only sent to super_admin / admin / master. */
export interface DashboardMoney {
  month_revenue: number;
  month_profit: number;
  month_margin: number | null;
  month_event_count: number;
  month_received: number;
  month_payments_count: number;
  month_expenses: number;
  month_vendor_payments: number;
  month_spent: number;
  month_net_cash: number;
  to_collect: number;
  to_collect_events: number;
  overdue: number;
  overdue_events: number;
  monthly: DashboardMonth[];
}

export interface DashboardData {
  today: string; // YYYY-MM-DD (server's today)
  month: { start: string; end: string };
  can_view_money: boolean;
  sections: { enquiries: boolean; quotations: boolean; tasks: boolean };
  counts: {
    today_events: number;
    today_guests: number;
    upcoming_events: number;
    month_events: number;
    month_events_ahead: number;
    open_enquiries: number | null;
    open_tasks: number | null;
    overdue_tasks: number | null;
  };
  today_events: DashboardEvent[];
  upcoming_events: DashboardEvent[];
  open_tasks: DashboardTask[];
  recent_quotations: DashboardQuotation[];
  money: DashboardMoney | null;
}

// ─── Small helpers shared by the dashboard widgets ─────────────────────────────

/** Parse YYYY-MM-DD as a local date (new Date("2026-09-22") would be UTC midnight). */
export function parseDay(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Whole days from `from` to `to` (both YYYY-MM-DD). */
export function daysBetween(from: string, to: string): number {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** "22 Sep" */
export function shortDay(value: string | null | undefined): string {
  const d = parseDay(value);
  return d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—";
}

/** "22 Sep" or "22–24 Sep" or "30 Sep – 2 Oct" */
export function dayRange(start: string | null, end: string | null): string {
  if (!start) return "—";
  if (!end || end === start) return shortDay(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${Number(start.slice(8, 10))}–${shortDay(end)}`;
  return `${shortDay(start)} – ${shortDay(end)}`;
}

export const statusLabel = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

export const plural = (n: number, word: string) => `${n.toLocaleString("en-IN")} ${word}${n === 1 ? "" : "s"}`;

// ─── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Loads the dashboard in one request and refreshes it quietly every `refreshMs`
 * while the tab is visible. A failed background refresh keeps the last data on screen.
 */
export function useDashboard(refreshMs = 60_000) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const hasData = useRef(false);
  const inFlight = useRef(false);
  const lastLoad = useRef(0);

  const load = useCallback(async (mode: "initial" | "manual" | "background") => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (mode === "initial") setLoading(true);
    if (mode === "manual") setRefreshing(true);
    try {
      const res = await dashboardApi.getOverview();
      if (!res?.success || !res?.data) throw new Error(res?.message || "The server sent an unexpected reply");
      setData(res.data as DashboardData);
      hasData.current = true;
      setUpdatedAt(new Date());
      setError(null);
      setRefreshError(null);
    } catch (err) {
      const message = apiErrorMessage(err, "Couldn't load the dashboard");
      if (hasData.current) setRefreshError(message);
      else setError(message);
    } finally {
      lastLoad.current = Date.now();
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load("initial");
  }, [load]);

  useEffect(() => {
    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (Date.now() - lastLoad.current >= refreshMs - 1000) load("background");
    };
    const timer = window.setInterval(tick, refreshMs);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load, refreshMs]);

  const retry = useCallback(() => load(hasData.current ? "manual" : "initial"), [load]);

  return { data, loading, refreshing, error, refreshError, updatedAt, retry };
}
